// Espejo exacto del cálculo de `registrar_venta`: rebaja por línea, reparto del descuento a la venta por
// mayor residuo e impuesto por tributo sobre su base. Todo en pesos enteros, sin coma flotante.
import type { DescuentoDeVenta, LineaMostrador, TotalesVenta } from './tipos';

/** `round(numerador / denominador)` de Postgres para enteros no negativos: la mitad sube. */
function redondearCociente(numerador: bigint, denominador: bigint): number {
  const q = numerador / denominador;
  const r = numerador % denominador;
  return Number(r * 2n >= denominador ? q + 1n : q);
}

/** Un valor de hasta dos decimales, en centésimas exactas. */
function centesimas(valor: number): bigint {
  return BigInt(Math.round(valor * 100));
}

function brutoDe(l: LineaMostrador): number {
  return redondearCociente(BigInt(Math.round(l.cantidad * 100)) * BigInt(l.precioUnit), 100n);
}

/**
 * Lo que rebaja el descuento propio de cada línea, acotado a su bruto. El máximo de unidades se consume en
 * orden de línea; un lleva y paga junta las unidades enteras de sus líneas y regala las más baratas.
 */
function rebajasDeLineas(lineas: LineaMostrador[]): number[] {
  const usadas = new Map<string, bigint>();
  const rebajas = lineas.map((l) => {
    const d = l.descuento;
    if (!d) return 0;
    const bruto = brutoDe(l);
    const cantidad = BigInt(Math.round(l.cantidad * 100));
    let quedan = cantidad;
    if (d.maxUnidades != null) {
      const previas = usadas.get(d.descuentoId) ?? 0n;
      const libres = BigInt(d.maxUnidades) * 100n - previas;
      quedan = libres <= 0n ? 0n : libres < cantidad ? libres : cantidad;
      usadas.set(d.descuentoId, previas + quedan);
    }
    let rebaja = 0;
    if (d.tipo === 'pct' && d.valor > 0 && d.valor <= 100) {
      const base = quedan === cantidad ? bruto : redondearCociente(quedan * BigInt(l.precioUnit), 100n);
      rebaja = redondearCociente(BigInt(base) * centesimas(d.valor), 10000n);
    } else if (d.tipo === 'monto_unidad' && d.valor > 0) {
      rebaja = redondearCociente(quedan * centesimas(d.valor), 10000n);
    } else if (d.tipo === 'precio_unidad' && d.valor >= 0 && d.valor < l.precioUnit) {
      rebaja = bruto - redondearCociente(cantidad * centesimas(d.valor), 10000n);
    }
    return Math.min(rebaja, bruto);
  });

  const promociones = new Map<string, number[]>();
  lineas.forEach((l, i) => {
    if (l.descuento?.tipo !== 'lleva_paga') return;
    promociones.set(l.descuento.descuentoId, [...(promociones.get(l.descuento.descuentoId) ?? []), i]);
  });
  for (const indices of promociones.values()) {
    const d = lineas[indices[0]!]!.descuento!;
    const lleva = d.lleva ?? 0;
    const paga = d.paga ?? 0;
    if (lleva <= paga || paga < 1) continue;
    const unidades = indices
      .flatMap((i) => Array.from({ length: Math.floor(lineas[i]!.cantidad) }, () => i))
      .sort((a, b) => lineas[a]!.precioUnit - lineas[b]!.precioUnit || a - b);
    let gratis = Math.floor(unidades.length / lleva) * (lleva - paga);
    if (d.maxVeces != null) gratis = Math.min(gratis, d.maxVeces * (lleva - paga));
    const porLinea = new Map<number, number>();
    for (const i of unidades.slice(0, gratis)) porLinea.set(i, (porLinea.get(i) ?? 0) + 1);
    for (const [i, n] of porLinea) rebajas[i] = Math.min(brutoDe(lineas[i]!), n * lineas[i]!.precioUnit);
  }
  return rebajas;
}

/** La rebaja de una línea sola, sin las demás de la venta. */
export function rebajaDeLinea(l: LineaMostrador): number {
  return rebajasDeLineas([l])[0]!;
}

/** Lo que el cálculo necesita del descuento a la venta. */
type ReglaDeVenta = Pick<DescuentoDeVenta, 'tipo' | 'valor' | 'libre'>;

/** Pesos que rebaja el descuento a la venta sobre `neto`; null si el pedido no vale. */
function rebajaDeVenta(neto: number, d: ReglaDeVenta): number | null {
  if (d.tipo === 'pct' && d.valor > 0 && d.valor <= 100) {
    return redondearCociente(BigInt(neto) * centesimas(d.valor), 10000n);
  }
  if (d.tipo === 'monto' && !d.libre && d.valor > 0) return Math.min(d.valor, neto);
  if (d.tipo === 'monto' && d.valor > 0 && Number.isInteger(d.valor) && d.valor <= neto) return d.valor;
  return null;
}

export interface LineaDesglosada {
  bruto: number;
  /** Rebaja propia del producto. */
  rebaja: number;
  /** Su parte del descuento a la venta. */
  reparto: number;
  neto: number;
  impuesto: number;
  /** Cada tributo de la línea con lo que aporta, en el orden del producto. */
  impuestos: { nombre: string; monto: number }[];
}

interface VentaDesglosada extends TotalesVenta {
  lineas: LineaDesglosada[];
}

/** La venta completa como la calcula el servidor. Un descuento a la venta que no vale no se aplica. */
export function desglosarVenta(lineas: LineaMostrador[], descuento?: ReglaDeVenta | null): VentaDesglosada {
  const brutos = lineas.map(brutoDe);
  const rebajas = rebajasDeLineas(lineas);
  const netos = brutos.map((b, i) => b - rebajas[i]!);
  const sumaNeto = netos.reduce((s, n) => s + n, 0);
  const descuentoVenta = descuento ? (rebajaDeVenta(sumaNeto, descuento) ?? 0) : 0;

  // Mayor residuo: cada línea recibe el entero de su proporción; los pesos que sobran van de a uno a los
  // residuos mayores, y el empate lo gana la primera.
  const repartos = netos.map(() => 0);
  if (sumaNeto > 0 && descuentoVenta > 0) {
    const d = BigInt(descuentoVenta);
    const total = BigInt(sumaNeto);
    const restos = netos.map((n, i) => {
      const producto = d * BigInt(n);
      repartos[i] = Number(producto / total);
      return { i, resto: producto % total };
    });
    let sobran = descuentoVenta - repartos.reduce((s, r) => s + r, 0);
    restos.sort((a, b) => (a.resto === b.resto ? a.i - b.i : a.resto > b.resto ? -1 : 1));
    for (const { i } of restos) {
      if (sobran === 0) break;
      repartos[i]! += 1;
      sobran -= 1;
    }
  }

  const desglose = lineas.map((l, i) => {
    const neto = netos[i]! - repartos[i]!;
    const impuestos = l.impuestos.map((imp) => {
      const base = imp.reduceBase ? neto : brutos[i]!;
      return { nombre: imp.nombre, monto: redondearCociente(BigInt(base) * centesimas(imp.tarifa), 10000n) };
    });
    const impuesto = impuestos.reduce((s, imp) => s + imp.monto, 0);
    return { bruto: brutos[i]!, rebaja: rebajas[i]!, reparto: repartos[i]!, neto, impuesto, impuestos };
  });
  const subtotal = desglose.reduce((s, l) => s + l.neto, 0);
  const impuesto = desglose.reduce((s, l) => s + l.impuesto, 0);
  return {
    lineas: desglose,
    bruto: brutos.reduce((s, b) => s + b, 0),
    descuentoLineas: rebajas.reduce((s, r) => s + r, 0),
    descuentoVenta,
    subtotal,
    impuesto,
    total: subtotal + impuesto,
  };
}

/**
 * La rebaja en pesos más grande cuyo total no pase de `objetivo` («dejar en $»). Con impuestos el total
 * salta de a varios pesos, así que puede quedar un poco por debajo. Null si ni sin rebaja se llega.
 */
export function llevarATotal(lineas: LineaMostrador[], objetivo: number): number | null {
  const sinRebaja = desglosarVenta(lineas);
  if (sinRebaja.total <= objetivo) return 0;
  const neto = sinRebaja.subtotal;
  let bajo = 1;
  let alto = neto;
  let mejor: number | null = null;
  while (bajo <= alto) {
    const medio = Math.floor((bajo + alto) / 2);
    if (desglosarVenta(lineas, { tipo: 'monto', valor: medio, libre: true }).total <= objetivo) {
      mejor = medio;
      alto = medio - 1;
    } else {
      bajo = medio + 1;
    }
  }
  return mejor;
}

